/**
 * ProductoSearchInput
 * Buscador ligero sobre la tabla "productos".
 * Props:
 *   value          – texto visible en el input (display)
 *   onChange(e)    – evento sintético { target: { name, value } }
 *   onProductoSelect(producto)  – callback con objeto completo { id, tipo, producto, precio_lista, precio_efectivo }
 *   name           – nombre del campo (para el evento sintético)
 *   placeholder    – placeholder del input
 *   tipoDefault    – tipo que se usará al crear un nuevo producto (ej: "Fornituras")
 */
import React, { useState, useEffect, useRef } from 'react';
import { obtenerProductos, agregarProducto } from '../api/backend_v2';
import { MagnifyingGlassIcon, PlusIcon, CheckIcon } from '@heroicons/react/24/outline';

const ProductoSearchInput = ({
  name = 'producto',
  value = '',
  onChange,
  onProductoSelect,
  placeholder = 'Buscar producto...',
  tipoDefault = 'Fornituras',
}) => {
  const [todos, setTodos] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [inputValue, setInputValue] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creando, setCreando] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Cargar productos al montar
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    obtenerProductos()
      .then((data) => {
        if (!cancelled) {
          const lista = Array.isArray(data) ? data : (data.productos || []);
          setTodos(lista);
          setFiltrados(lista);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Sincronizar prop value
  useEffect(() => { setInputValue(value ?? ''); }, [value]);

  // Filtrar al escribir
  useEffect(() => {
    const q = (inputValue ?? '').trim().toLowerCase();
    if (!q) { setFiltrados(todos); return; }
    setFiltrados(
      todos.filter((p) =>
        (p.producto ?? '').toLowerCase().includes(q) ||
        (p.tipo ?? '').toLowerCase().includes(q)
      )
    );
  }, [inputValue, todos]);

  // Cerrar al click fuera
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    setShowDropdown(true);
    onChange?.({ target: { name, value: v } });
  };

  const selectProducto = (p) => {
    const display = p.producto;
    setInputValue(display);
    setShowDropdown(false);
    onChange?.({ target: { name, value: display } });
    onProductoSelect?.(p);
  };

  const crearNuevo = async () => {
    const nombre = (inputValue ?? '').trim();
    if (!nombre || creando) return;
    setCreando(true);
    try {
      const respuesta = await agregarProducto({
        tipo: tipoDefault,
        producto: nombre,
        precio_lista: null,
        precio_efectivo: null,
      });
      // La API devuelve el objeto producto directamente (tiene campo `id`)
      const obj = (respuesta?.id != null)
        ? respuesta
        : { id: null, tipo: tipoDefault, producto: nombre };
      setTodos((prev) => [...prev, obj]);
      selectProducto(obj);
    } catch (_) {
      selectProducto({ id: null, tipo: tipoDefault, producto: nombre });
    } finally {
      setCreando(false);
    }
  };

  const hayCoincidenciaExacta = todos.some(
    (p) => (p.producto ?? '').toLowerCase() === (inputValue ?? '').trim().toLowerCase()
  );
  const mostrarCrear = (inputValue ?? '').trim().length > 0 && !hayCoincidenciaExacta;

  return (
    <div ref={containerRef} className="relative">
      {/* Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={loading ? 'Cargando...' : placeholder}
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (filtrados.length > 0 || mostrarCrear) && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg mt-0.5 max-h-48 overflow-y-auto z-50"
        >
          {filtrados.map((p) => (
            <button
              key={p.id ?? p.producto}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); selectProducto(p); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
            >
              <span className="text-sm font-medium text-gray-800">{p.producto}</span>
              {p.tipo && (
                <span className="ml-2 text-xs text-gray-400">{p.tipo}</span>
              )}
              {p.precio_lista != null && (
                <span className="ml-2 text-xs text-gray-500">
                  ${Number(p.precio_lista).toLocaleString('es-AR')}
                </span>
              )}
            </button>
          ))}

          {/* Crear nuevo */}
          {mostrarCrear && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); crearNuevo(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 transition-colors font-medium"
            >
              {creando ? (
                <CheckIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PlusIcon className="h-4 w-4" />
              )}
              {creando ? 'Creando...' : `Crear "${inputValue.trim()}" como ${tipoDefault}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductoSearchInput;
